import { useEffect, useRef, useState } from "react";
import { Button, Field, SelectField, Spinner, StatusBadge } from "@leylion/design-system";
import { BRAND } from "../brand";
import {
  getPlatformConsoleOverview,
  getPlatformPlans,
  getPlatformProvisioning,
  getPlatformTenant,
  getPlatformTenants
} from "./platform-client";
import type {
  PlatformConsoleOverview,
  PlatformPlanSummary,
  PlatformProvisioningSummary,
  PlatformTenantDetail,
  PlatformTenantFilters,
  PlatformTenantSummary
} from "./platform-types";
import { PlatformExecutivePanel, executiveRefreshEvent } from "./PlatformExecutivePanel";
import type { ExecutiveTarget } from "./platform-executive-client";
import type { PlatformAuditFilters } from "./platform-audit-types";
import { PlatformAuditPanel } from "./PlatformAuditPanel";
import { PlatformOperationsPanel, operationsRefreshEvent } from "./PlatformOperationsPanel";
import { PlatformPlanAdminPanel } from "./PlatformPlanAdminPanel";
import { PlatformRegistrationControlCenter } from "./PlatformRegistrationControlCenter";
import { PlatformSubscriptionAdminPanel } from "./PlatformSubscriptionAdminPanel";
import "./platform-console.css";

type Language = "ar" | "en";
type PlatformPage = "overview" | "tenants" | "provisioning" | "plans" | "audit" | "operations";
type Tone = "success" | "warning" | "danger" | "info" | "neutral";

const auditRefreshEvent = "leylion-platform-audit-refresh";

function formatDate(value: string | null, language: Language): string {
  if (!value) return "—";
  return new Date(value).toLocaleString(language === "ar" ? "ar-AE" : "en-AE", {
    dateStyle: "medium",
    timeStyle: "short"
  });
}

function toneForStatus(status: string | null): Tone {
  if (!status) return "neutral";
  const normalized = status.toLowerCase();
  if (["active", "succeeded", "completed", "full"].includes(normalized)) return "success";
  if (["trial", "pending", "running", "inprogress", "verificationpending"].includes(normalized)) return "info";
  if (["grace", "suspended", "pastdue", "failedretryable", "expired", "restricted"].includes(normalized)) return "warning";
  if (["failed", "failedpermanent", "blocked", "cancelled"].includes(normalized)) return "danger";
  return "neutral";
}

function statusLabel(status: string | null, ar: boolean): string {
  if (!status) return "—";
  if (!ar) return status;
  const labels: Record<string, string> = {
    Active: "فعّالة",
    Suspended: "موقوفة",
    Failed: "فاشلة",
    Archived: "مؤرشفة",
    Provisioning: "قيد التأسيس",
    Trial: "تجريبية",
    PastDue: "متأخرة",
    Cancelled: "ملغاة",
    Expired: "منتهية",
    Full: "كامل",
    Grace: "فترة سماح",
    Restricted: "مقيّد",
    Blocked: "محظور",
    InProgress: "قيد الإعداد",
    Completed: "مكتمل",
    NotStarted: "لم يبدأ",
    Pending: "معلّق",
    Skipped: "تم التجاوز",
    Running: "قيد التنفيذ",
    Succeeded: "ناجح",
    FailedRetryable: "فشل قابل للإعادة",
    FailedPermanent: "فشل نهائي"
  };
  return labels[status] ?? status;
}

function onboardingStepLabel(stepKey: string, ar: boolean): string {
  const labels: Record<string, { ar: string; en: string }> = {
    business_identity: { ar: "هوية المؤسسة", en: "Business identity" },
    first_branch: { ar: "الفرع الأول", en: "First branch" },
    localization_tax: { ar: "اللغة والضريبة", en: "Localization & tax" },
    inventory_baseline: { ar: "أساس المخزون", en: "Inventory baseline" },
    business_hours: { ar: "ساعات العمل", en: "Business hours" },
    security: { ar: "الأمان ورمز المدير", en: "Security & manager PIN" },
    catalog: { ar: "المنتجات", en: "Catalog" },
    payments_devices: { ar: "المدفوعات والأجهزة", en: "Payments & devices" },
    currency: { ar: "العملة الأساسية", en: "Base currency" }
  };
  return labels[stepKey]?.[ar ? "ar" : "en"] ?? stepKey;
}

function usageText(value: number, limit: number | null): string {
  return limit === null ? `${value} / ∞` : `${value} / ${limit}`;
}

export function PlatformConsole({
  token,
  language,
  onToggleLanguage,
  onOpenAdministration,
  onExit,
  onLogout
}: {
  token: string;
  language: Language;
  onToggleLanguage: () => void;
  onOpenAdministration: () => void;
  onExit: () => void;
  onLogout: () => void;
}) {
  const ar = language === "ar";
  const tenantRequest = useRef(0);
  const [executiveOrigin, setExecutiveOrigin] = useState<string | null>(null);
  const [returnFocusId, setReturnFocusId] = useState<string>();
  const [registrationEntry, setRegistrationEntry] = useState<{ id?: string; attentionOnly?: boolean }>({});
  const [auditEntry, setAuditEntry] = useState<PlatformAuditFilters>({});
  const [page, setPage] = useState<PlatformPage>("overview");
  const [overview, setOverview] = useState<PlatformConsoleOverview | null>(null);
  const [tenants, setTenants] = useState<PlatformTenantSummary[]>([]);
  const [provisioning, setProvisioning] = useState<PlatformProvisioningSummary[]>([]);
  const [plans, setPlans] = useState<PlatformPlanSummary[]>([]);
  const [auditCount, setAuditCount] = useState(0);
  const [filters, setFilters] = useState<PlatformTenantFilters>({});
  const [selectedTenant, setSelectedTenant] = useState<PlatformTenantDetail | null>(null);
  const [tenantBusy, setTenantBusy] = useState(false);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState("");

  const refresh = async () => {
    if (page === "operations") {
      window.dispatchEvent(new CustomEvent(operationsRefreshEvent));
      return;
    }
    if (page === "overview") {
      window.dispatchEvent(new CustomEvent(executiveRefreshEvent));
      window.dispatchEvent(new CustomEvent(operationsRefreshEvent));
    }
    setBusy(true);
    setError("");
    try {
      const [overviewValue, tenantItems, provisioningItems, planItems] = await Promise.all([
        getPlatformConsoleOverview(token),
        getPlatformTenants(token, filters),
        getPlatformProvisioning(token),
        getPlatformPlans(token)
      ]);
      setOverview(overviewValue);
      setTenants(tenantItems);
      setProvisioning(provisioningItems);
      setPlans(planItems);
      if (selectedTenant) {
        const sequence = tenantRequest.current;
        const updated = await getPlatformTenant(token, selectedTenant.tenantId);
        setSelectedTenant(current => sequence === tenantRequest.current && current?.tenantId === selectedTenant.tenantId ? updated : current);
      }
      if (page === "audit") {
        window.dispatchEvent(new CustomEvent(auditRefreshEvent));
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Platform Console request failed.");
    } finally {
      setBusy(false);
    }
  };

  const refreshTenants = async () => {
    setTenantBusy(true);
    setError("");
    try {
      setTenants(await getPlatformTenants(token, filters));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Tenant directory request failed.");
    } finally {
      setTenantBusy(false);
    }
  };

  const openTenant = async (tenantId: string) => {
    const sequence = ++tenantRequest.current;
    setTenantBusy(true);
    setSelectedTenant(current => current?.tenantId === tenantId ? current : null);
    setError("");
    try {
      const tenant = await getPlatformTenant(token, tenantId);
      if (sequence === tenantRequest.current) setSelectedTenant(tenant);
    } catch (caught) {
      if (sequence === tenantRequest.current) setError(caught instanceof Error ? caught.message : "Tenant 360 request failed.");
    } finally {
      if (sequence === tenantRequest.current) setTenantBusy(false);
    }
  };

  useEffect(() => {
    void refresh();
    return () => { tenantRequest.current++; };
  }, [token]);

  useEffect(() => {
    document.title = `${BRAND.productName} Platform Console`;
    return () => {
      document.title = `${BRAND.productName} Enterprise`;
    };
  }, []);

  const navigation: Array<{ id: PlatformPage; ar: string; en: string; count?: number }> = [
    { id: "overview", ar: "نظرة عامة", en: "Overview" },
    { id: "tenants", ar: "المؤسسات", en: "Tenants", count: tenants.length },
    { id: "provisioning", ar: "التأسيس", en: "Provisioning", count: provisioning.length },
    { id: "plans", ar: "الباقات", en: "Plans", count: plans.length },
    { id: "audit", ar: "سجل المنصة", en: "Platform Audit", count: auditCount },
    { id: "operations", ar: "التشغيل", en: "Operations" }
  ];

  const changePage = (target: PlatformPage) => {
    tenantRequest.current++;
    setTenantBusy(false);
    setError("");
    setExecutiveOrigin(null);
    setReturnFocusId(undefined);
    setRegistrationEntry({});
    setAuditEntry({});
    setPage(target);
    if (target !== "tenants") setSelectedTenant(null);
  };

  const openExecutiveTarget = (target: ExecutiveTarget) => {
    const origin = document.activeElement instanceof HTMLElement
      ? document.activeElement.dataset.executiveTarget ?? "workspace" : "workspace";
    changePage(target.kind === "tenant" || target.kind === "tenants" ? "tenants"
      : target.kind === "registration" || target.kind === "registrations" ? "provisioning"
        : target.kind === "notificationAudit" ? "audit" : "operations");
    setExecutiveOrigin(origin);
    if (target.kind === "tenant") void openTenant(target.id);
    if (target.kind === "tenants") {
      setFilters({});
      setTenantBusy(true);
      const sequence = ++tenantRequest.current;
      void getPlatformTenants(token, {}).then(items => {
        if (sequence === tenantRequest.current) setTenants(items);
      }).catch(() => {
        if (sequence === tenantRequest.current) setError(ar ? "تعذر تحميل دليل المؤسسات." : "Tenant directory unavailable.");
      }).finally(() => { if (sequence === tenantRequest.current) setTenantBusy(false); });
    }
    if (target.kind === "registration") setRegistrationEntry({ id: target.id });
    if (target.kind === "registrations") setRegistrationEntry({ attentionOnly: true });
    if (target.kind === "notificationAudit") setAuditEntry({ action: "SignupVerificationDeliveryFailed" });
  };

  const returnToExecutive = () => {
    const origin = executiveOrigin;
    changePage("overview");
    setReturnFocusId(origin ?? undefined);
  };

  return (
    <div className="platform-console" dir={ar ? "rtl" : "ltr"}>
      <aside className="platform-sidebar">
        <div className="platform-sidebar__brand">
          <img src={BRAND.assets.wordmarkLight} alt={BRAND.productName} />
          <span>PLATFORM CONSOLE</span>
        </div>
        <nav aria-label={ar ? "تنقل منصة ليليون" : "Leylion platform navigation"}>
          {navigation.map((item) => (
            <button
              key={item.id}
              type="button"
              className={page === item.id ? "is-active" : ""}
              onClick={() => changePage(item.id)}
            >
              <span>{ar ? item.ar : item.en}</span>
              {item.count !== undefined ? <b>{item.count}</b> : null}
            </button>
          ))}
        </nav>
        <div className="platform-sidebar__boundary">
          <strong>{ar ? "حدود المنصة" : "Platform boundary"}</strong>
          <span>{ar ? "Control Plane فقط · بدون سياق مؤسسة أو فرع" : "Control plane only · no tenant or branch context"}</span>
        </div>
      </aside>

      <div className="platform-main">
        <header className="platform-topbar">
          <div>
            <span className="platform-kicker">LEYLION CONTROL PLANE</span>
            <strong>{ar ? "مالك المنصة" : "Platform Owner"}</strong>
          </div>
          <div className="platform-topbar__actions">
            <Button variant="secondary" type="button" onClick={() => void refresh()}>
              {ar ? "تحديث" : "Refresh"}
            </Button>
            <Button variant="secondary" type="button" onClick={onToggleLanguage}>
              {ar ? "EN" : "AR"}
            </Button>
            <button
              className="root-platform-admin-launcher"
              data-testid="platform-admin-launcher"
              type="button"
              onClick={onOpenAdministration}
            >
              {ar ? "إدارة ملاك المنصة والجلسات" : "PlatformOwners & sessions"}
            </button>
            <Button variant="secondary" type="button" onClick={onExit}>
              {ar ? "العودة للمؤسسات" : "Business contexts"}
            </Button>
            <Button variant="secondary" type="button" onClick={onLogout}>
              {ar ? "تسجيل الخروج" : "Log out"}
            </Button>
          </div>
        </header>

        <main className="platform-content">
          <div className="platform-heading">
            <div>
              <span className="platform-kicker">{ar ? "منصة الإدارة المركزية" : "CENTRAL CONTROL PLANE"}</span>
              <h1>{selectedTenant && page === "tenants"
                ? selectedTenant.tradeName
                : navigation.find((item) => item.id === page)?.[ar ? "ar" : "en"]}</h1>
              <p>{selectedTenant && page === "tenants"
                ? ar
                  ? "Tenant 360 · ملف موحد للمؤسسة من Control Plane بدون الدخول إلى بيانات التشغيل."
                  : "Tenant 360 · unified control-plane profile without entering tenant operational data."
                : ar
                  ? "رؤية موثوقة لحالة المؤسسات والاشتراكات والتأسيس وسجل المنصة دون الدخول إلى بيانات تشغيل أي مؤسسة."
                  : "Trusted visibility into tenants, subscriptions, provisioning and platform audit without entering any tenant's operational data."}</p>
            </div>
            <StatusBadge tone="success">PlatformOwner</StatusBadge>
          </div>

          {page !== "operations" && page !== "overview" && error ? <div className="platform-error" role="alert">{error}</div> : null}
          {page !== "operations" && page !== "overview" && busy && !overview ? <div className="platform-loading"><Spinner label={ar ? "جارٍ تحميل المنصة" : "Loading platform"} /></div> : null}

          {page === "operations" ? <PlatformOperationsPanel key={token} token={token} language={language} /> : null}

          {executiveOrigin ? <Button variant="secondary" onClick={returnToExecutive}>{ar ? "العودة إلى الملخص" : "Back to summary"}</Button> : null}
          {page === "overview" ? <PlatformExecutivePanel key={token} token={token} language={language} onNavigate={openExecutiveTarget} returnFocusId={returnFocusId} /> : null}

          {page === "tenants" && !selectedTenant ? (
            <section className="platform-panel">
              <div className="platform-panel__toolbar platform-panel__toolbar--directory">
                <PanelTitle title={ar ? "دليل المؤسسات" : "Tenant directory"} subtitle={ar ? "الاسم التجاري والمالك وحالة الوصول والاستخدام من Control Plane" : "Business identity, owner, access and usage from the control plane"} />
                <div className="platform-directory-filters">
                  <Field
                    label={ar ? "بحث" : "Search"}
                    value={filters.search ?? ""}
                    onChange={(event) => setFilters((value) => ({ ...value, search: event.target.value }))}
                    placeholder={ar ? "اسم، مالك، بريد أو Tenant ID" : "Name, owner, email or tenant ID"}
                  />
                  <SelectField label={ar ? "دورة المؤسسة" : "Lifecycle"} value={filters.lifecycleStatus ?? ""} onChange={(event) => setFilters((value) => ({ ...value, lifecycleStatus: event.target.value }))}>
                    <option value="">{ar ? "الكل" : "All"}</option><option value="Active">{ar ? "فعّالة" : "Active"}</option><option value="Suspended">{ar ? "موقوفة" : "Suspended"}</option><option value="Failed">{ar ? "فاشلة" : "Failed"}</option><option value="Archived">{ar ? "مؤرشفة" : "Archived"}</option>
                  </SelectField>
                  <SelectField label={ar ? "الوصول" : "Access"} value={filters.accessMode ?? ""} onChange={(event) => setFilters((value) => ({ ...value, accessMode: event.target.value }))}>
                    <option value="">{ar ? "الكل" : "All"}</option><option value="Full">{ar ? "كامل" : "Full"}</option><option value="Grace">{ar ? "فترة سماح" : "Grace"}</option><option value="Restricted">{ar ? "مقيّد" : "Restricted"}</option><option value="Blocked">{ar ? "محظور" : "Blocked"}</option>
                  </SelectField>
                  <SelectField label={ar ? "الاشتراك" : "Subscription"} value={filters.subscriptionStatus ?? ""} onChange={(event) => setFilters((value) => ({ ...value, subscriptionStatus: event.target.value }))}>
                    <option value="">{ar ? "الكل" : "All"}</option><option value="Trial">{ar ? "تجريبي" : "Trial"}</option><option value="Active">{ar ? "فعّال" : "Active"}</option><option value="PastDue">{ar ? "متأخر" : "Past due"}</option><option value="Suspended">{ar ? "موقوف" : "Suspended"}</option><option value="Expired">{ar ? "منتهي" : "Expired"}</option>
                  </SelectField>
                  <Button type="button" disabled={tenantBusy} onClick={() => void refreshTenants()}>{tenantBusy ? "…" : ar ? "تطبيق" : "Apply"}</Button>
                </div>
              </div>
              <div className="platform-table-wrap">
                <table className="platform-table platform-table--tenants">
                  <thead><tr>
                    <th>{ar ? "المؤسسة" : "Tenant"}</th><th>{ar ? "المالك" : "Owner"}</th><th>{ar ? "الوصول" : "Access"}</th><th>{ar ? "الاشتراك" : "Subscription"}</th><th>{ar ? "الاستخدام" : "Usage"}</th><th>{ar ? "الإعداد" : "Onboarding"}</th><th>{ar ? "آخر تحديث" : "Updated"}</th>
                  </tr></thead>
                  <tbody>{tenants.map((tenant) => (
                    <tr key={tenant.tenantId} className="platform-table__clickable" onClick={() => void openTenant(tenant.tenantId)}>
                      <td><strong>{tenant.tradeName}</strong><small>{tenant.legalName ?? tenant.tenantId}</small><small>{[tenant.countryCode, tenant.currency].filter(Boolean).join(" · ")}</small></td>
                      <td><strong>{tenant.ownerDisplayName ?? "—"}</strong><small>{tenant.ownerEmail ?? "—"}</small></td>
                      <td><StatusBadge tone={toneForStatus(tenant.accessMode)}>{statusLabel(tenant.accessMode, ar)}</StatusBadge><small>{statusLabel(tenant.lifecycleStatus, ar)}</small></td>
                      <td><StatusBadge tone={toneForStatus(tenant.subscriptionStatus)}>{statusLabel(tenant.subscriptionStatus, ar)}</StatusBadge><small>{tenant.planCode ?? "—"}{tenant.planVersionNumber ? ` · v${tenant.planVersionNumber}` : ""}</small></td>
                      <td><strong>{ar ? `${tenant.branchCount} فروع` : `${tenant.branchCount} branches`}</strong><small>{ar ? `${tenant.userCount} مستخدم · ${tenant.deviceCount} جهاز` : `${tenant.userCount} users · ${tenant.deviceCount} devices`}</small></td>
                      <td><StatusBadge tone={tenant.onboardingNeedsAttention ? "danger" : toneForStatus(tenant.onboardingStatus)}>{statusLabel(tenant.onboardingStatus, ar)}</StatusBadge><small>{tenant.onboardingCompletionPercent !== null ? `${tenant.onboardingCompletionPercent}%` : ""}{tenant.onboardingNeedsAttention ? (ar ? " · يحتاج انتباه" : " · Needs attention") : ""}</small></td>
                      <td>{formatDate(tenant.updatedAt, language)}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            </section>
          ) : null}

          {page === "tenants" && selectedTenant ? (
            <Tenant360
              tenant={selectedTenant}
              token={token}
              plans={plans}
              language={language}
              busy={tenantBusy}
              onBack={() => setSelectedTenant(null)}
              onRefresh={() => void openTenant(selectedTenant.tenantId)}
            />
          ) : null}

          {page === "provisioning" ? (
            <PlatformRegistrationControlCenter key={`${token}:${registrationEntry.id ?? ""}:${registrationEntry.attentionOnly ?? false}`} token={token} language={language} initialRegistrationId={registrationEntry.id} initialAttentionOnly={registrationEntry.attentionOnly} />
          ) : null}

          {page === "plans" ? (
            <>
              <section className="platform-plan-grid">
                {plans.map((plan) => <article className="platform-plan-card" key={plan.planId}>
                  <div><span className="platform-kicker">{plan.code}</span><h2>{ar ? plan.nameAr : plan.nameEn}</h2></div>
                  <div className="platform-plan-card__badges"><StatusBadge tone={plan.isActive ? "success" : "neutral"}>{plan.isActive ? (ar ? "فعالة" : "Active") : (ar ? "غير فعالة" : "Inactive")}</StatusBadge><StatusBadge tone={plan.isPublic ? "info" : "neutral"}>{plan.isPublic ? (ar ? "عامة" : "Public") : (ar ? "خاصة" : "Private")}</StatusBadge></div>
                  <dl><div><dt>{ar ? "الإصدار" : "Version"}</dt><dd>{plan.currentVersionNumber ? `v${plan.currentVersionNumber}` : "—"}</dd></div><div><dt>{ar ? "التفعيل" : "Activation"}</dt><dd>{statusLabel(plan.activationMode, ar)}</dd></div><div><dt>{ar ? "أيام التجربة" : "Trial days"}</dt><dd>{plan.trialDays ?? "—"}</dd></div></dl>
                </article>)}
              </section>
              <PlatformPlanAdminPanel token={token} language={language} plans={plans} onChanged={refresh} />
            </>
          ) : null}

          {page === "audit" ? (
            <PlatformAuditPanel key={`${token}:${auditEntry.action ?? ""}`} token={token} language={language} entryFilters={auditEntry} onCountChange={setAuditCount} />
          ) : null}
        </main>
      </div>
    </div>
  );
}

function Tenant360({
  tenant,
  token,
  plans,
  language,
  busy,
  onBack,
  onRefresh
}: {
  tenant: PlatformTenantDetail;
  token: string;
  plans: PlatformPlanSummary[];
  language: Language;
  busy: boolean;
  onBack: () => void;
  onRefresh: () => void;
}) {
  const ar = language === "ar";
  const subscription = tenant.subscription;
  return (
    <div className="tenant360">
      <div className="tenant360__actions">
        <Button variant="secondary" type="button" onClick={onBack}>{ar ? "العودة للدليل" : "Back to directory"}</Button>
        <Button variant="secondary" type="button" disabled={busy} onClick={onRefresh}>{busy ? "…" : ar ? "تحديث المؤسسة" : "Refresh tenant"}</Button>
      </div>

      <section className="tenant360-hero">
        <div>
          <span className="platform-kicker">TENANT 360</span>
          <h2>{tenant.tradeName}</h2>
          <p>{tenant.legalName ?? tenant.tenantId}</p>
          <small>{tenant.tenantId}</small>
        </div>
        <div className="tenant360-hero__badges">
          <StatusBadge tone={toneForStatus(tenant.lifecycleStatus)}>{statusLabel(tenant.lifecycleStatus, ar)}</StatusBadge>
          <StatusBadge tone={toneForStatus(subscription.accessMode)}>{statusLabel(subscription.accessMode, ar)}</StatusBadge>
          <StatusBadge tone={toneForStatus(subscription.effectiveSubscriptionStatus)}>{statusLabel(subscription.effectiveSubscriptionStatus, ar)}</StatusBadge>
        </div>
      </section>

      <section className="tenant360-grid tenant360-grid--three">
        <InfoCard title={ar ? "هوية المؤسسة" : "Business identity"} rows={[
          [ar ? "الاسم التجاري" : "Trade name", tenant.tradeName],
          [ar ? "الاسم القانوني" : "Legal name", tenant.legalName],
          [ar ? "الدولة" : "Country", tenant.countryCode],
          [ar ? "نوع النشاط" : "Business type", tenant.businessType],
          [ar ? "العملة" : "Currency", tenant.currency],
          [ar ? "المنطقة الزمنية" : "Time zone", tenant.timeZone]
        ]} />
        <InfoCard title={ar ? "المالك والتواصل" : "Owner & contact"} rows={[
          [ar ? "المالك" : "Owner", tenant.ownerDisplayName],
          [ar ? "بريد المالك" : "Owner email", tenant.ownerEmail],
          [ar ? "بريد المؤسسة" : "Business email", tenant.contactEmail],
          [ar ? "الهاتف" : "Phone", tenant.contactPhone]
        ]} />
        <InfoCard title={ar ? "الوصول ودورة المؤسسة" : "Access & lifecycle"} rows={[
          [ar ? "دورة المؤسسة" : "Lifecycle", statusLabel(tenant.lifecycleStatus, ar)],
          [ar ? "وضع الوصول" : "Access mode", statusLabel(subscription.accessMode, ar)],
          [ar ? "تم التفعيل" : "Activated", formatDate(tenant.activatedAt, language)],
          [ar ? "سبب التقييد" : "Reason", subscription.reasons.join(", ") || tenant.suspensionReasonCode]
        ]} />
      </section>

      <section className="platform-panel tenant360-subscription">
        <PanelTitle title={ar ? "الاشتراك والباقة" : "Subscription & plan"} subtitle={ar ? "الحالة الفعلية وقيود الخطة من نفس سياسة SaaS" : "Effective state and limits from the canonical SaaS policy"} />
        <div className="tenant360-subscription__grid">
          <Fact label={ar ? "الباقة" : "Plan"} value={`${subscription.planCode ?? "—"}${subscription.planVersionNumber ? ` · v${subscription.planVersionNumber}` : ""}`} />
          <Fact label={ar ? "الحالة المخزنة" : "Stored status"} value={statusLabel(subscription.storedSubscriptionStatus, ar)} />
          <Fact label={ar ? "الحالة الفعلية" : "Effective status"} value={statusLabel(subscription.effectiveSubscriptionStatus, ar)} />
          <Fact label={ar ? "نهاية التجربة" : "Trial ends"} value={formatDate(subscription.trialEndsAt, language)} />
          <Fact label={ar ? "نهاية الفترة" : "Period ends"} value={formatDate(subscription.currentPeriodEnd, language)} />
          <Fact label={ar ? "فترة السماح" : "Grace ends"} value={formatDate(subscription.graceEndsAt, language)} />
        </div>
      </section>

      <PlatformSubscriptionAdminPanel token={token} tenantId={tenant.tenantId} language={language} plans={plans} onChanged={onRefresh} />

      <section className="tenant360-grid tenant360-grid--three">
        <UsageCard label={ar ? "الفروع" : "Branches"} value={tenant.usage.branchCount} limit={tenant.usage.branchLimit} />
        <UsageCard label={ar ? "المستخدمون" : "Users"} value={tenant.usage.userCount} limit={tenant.usage.userLimit} />
        <UsageCard label={ar ? "الأجهزة" : "Devices"} value={tenant.usage.deviceCount} limit={tenant.usage.deviceLimit} />
      </section>

      <section className="tenant360-grid tenant360-grid--two">
        <InfoCard title={ar ? "الإعداد الأولي" : "Onboarding"} rows={[
          [ar ? "الحالة" : "Status", statusLabel(tenant.onboarding.status, ar)],
          [ar ? "الإنجاز" : "Completion", tenant.onboarding.completionPercent !== null ? `${tenant.onboarding.completionPercent}%` : null],
          [ar ? "الانتباه" : "Attention", tenant.onboarding.needsAttention ? (ar ? "يوجد عائق يحتاج مراجعة" : "Blocked step needs review") : (ar ? "لا يوجد عائق مسجل" : "No recorded blocker")],
          [ar ? "خطوات معلقة" : "Pending steps", tenant.onboarding.pendingSteps.map((step) => onboardingStepLabel(step, ar)).join(", ") || (ar ? "لا يوجد" : "None")],
          [ar ? "اكتمل" : "Completed", formatDate(tenant.onboarding.completedAt, language)]
        ]} />
        <InfoCard title={ar ? "التأسيس" : "Provisioning"} rows={[
          [ar ? "الحالة" : "Status", statusLabel(tenant.provisioning.status, ar)],
          [ar ? "المحاولات" : "Attempts", String(tenant.provisioning.attemptCount)],
          [ar ? "الفشل" : "Failure", tenant.provisioning.failureCode],
          [ar ? "اكتمل" : "Completed", formatDate(tenant.provisioning.completedAt, language)]
        ]} />
      </section>

      <section className="platform-panel">
        <PanelTitle title={ar ? "تشخيص الإعداد الأولي" : "Onboarding diagnostics"} subtitle={ar ? "حالة كل خطوة من Control Plane فقط؛ بدون عرض بيانات التشغيل أو DataJson الخام." : "Per-step control-plane state only; tenant operational data and raw DataJson are not exposed."} />
        <div className="platform-table-wrap">
          <table className="platform-table">
            <thead><tr>
              <th>{ar ? "الخطوة" : "Step"}</th>
              <th>{ar ? "الحالة" : "Status"}</th>
              <th>{ar ? "المطلوب" : "Required"}</th>
              <th>{ar ? "الإنجاز" : "Completion"}</th>
              <th>{ar ? "آخر تحديث" : "Updated"}</th>
              <th>{ar ? "اكتملت" : "Completed"}</th>
            </tr></thead>
            <tbody>
              {tenant.onboarding.steps.length === 0 ? (
                <tr><td colSpan={6}>{ar ? "لا توجد خطوات إعداد مسجلة." : "No onboarding steps are recorded."}</td></tr>
              ) : tenant.onboarding.steps.map((step) => (
                <tr key={step.stepKey}>
                  <td><strong>{onboardingStepLabel(step.stepKey, ar)}</strong><small>{step.stepKey}</small></td>
                  <td><StatusBadge tone={toneForStatus(step.status)}>{statusLabel(step.status, ar)}</StatusBadge></td>
                  <td>{step.isRequired ? (ar ? "نعم" : "Yes") : (ar ? "لا" : "No")}</td>
                  <td>{step.completionPercent}%</td>
                  <td>{formatDate(step.updatedAt, language)}</td>
                  <td>{formatDate(step.completedAt, language)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="platform-panel tenant360-timeline">
        <PanelTitle title={ar ? "الخط الزمني للمؤسسة" : "Tenant timeline"} subtitle={ar ? "أحداث SaaS وسجل المنصة المنقح فقط" : "SaaS events and redacted platform audit only"} />
        <div className="tenant360-timeline__list">
          {tenant.timeline.length === 0 ? <p>{ar ? "لا توجد أحداث." : "No events."}</p> : tenant.timeline.map((item, index) => (
            <article key={`${item.occurredAt}-${item.code}-${index}`}>
              <div><StatusBadge tone={item.kind === "Subscription" ? "info" : "neutral"}>{item.kind}</StatusBadge><strong>{item.code}</strong></div>
              <time>{formatDate(item.occurredAt, language)}</time>
              <code>{item.detail ?? "—"}</code>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}

function PanelTitle({ title, subtitle }: { title: string; subtitle: string }) {
  return <div className="platform-panel__title"><div><h2>{title}</h2><p>{subtitle}</p></div></div>;
}

function Fact({ label, value }: { label: string; value: string }) {
  return <div className="tenant360-fact"><span>{label}</span><strong>{value || "—"}</strong></div>;
}

function InfoCard({ title, rows }: { title: string; rows: Array<[string, string | null | undefined]> }) {
  return <article className="tenant360-card"><h3>{title}</h3><dl>{rows.map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{value || "—"}</dd></div>)}</dl></article>;
}

function UsageCard({ label, value, limit }: { label: string; value: number; limit: number | null }) {
  const percent = limit && limit > 0 ? Math.min(100, Math.round((value / limit) * 100)) : 0;
  return <article className="tenant360-usage"><span>{label}</span><strong>{usageText(value, limit)}</strong>{limit !== null ? <div className="tenant360-usage__bar"><i style={{ width: `${percent}%` }} /></div> : <small>Unlimited</small>}</article>;
}
